import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { CreditCard, Home, Info, LogOut, Settings, Shield, LayoutTemplate, Smartphone } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { Logo } from "../brand/Logo";
import { useAuth } from "../../context/AuthContext";
import { useCredits } from "../../context/CreditsContext";
import { isStandalone } from "../../hooks/useInstallPrompt";
import { trackAppTap } from "../../lib/appStats";

const LINKS = [["/home", "Home", Home], ["/billing", "Billing & credits", CreditCard], ["/settings", "Settings", Settings], ["/about", "About Ishukart", Info]];

export const NavDrawer = ({ open, onOpenChange, onTemplates }) => {
  const { user, logout } = useAuth();
  const { credits } = useCredits();
  const navigate = useNavigate();
  const item = "flex items-center gap-3 h-11 px-3 rounded-xl text-[15px] transition-colors";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] bg-white border-slate-200 p-5 flex flex-col" data-testid="nav-drawer">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <SheetDescription className="sr-only">Navigate Ishukart</SheetDescription>
        <Logo to="/home" />
        <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          {credits && <p className="mt-2 text-xs text-slate-600" data-testid="nav-drawer-credits"><span className="font-mono font-semibold text-slate-900">{credits.total}</span> credits left</p>}
        </div>
        <nav className="mt-4 flex-1 space-y-1">
          {LINKS.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end onClick={() => onOpenChange(false)} data-testid={`nav-drawer-${to.slice(1)}`}
              className={({ isActive }) => `${item} ${isActive ? "bg-sky-50 text-sky-700 font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
              <Icon size={18} strokeWidth={1.8} /> {label}
            </NavLink>
          ))}
          <button onClick={() => { onOpenChange(false); onTemplates?.(); }} data-testid="nav-drawer-templates" className={`${item} w-full text-slate-700 hover:bg-slate-50`}>
            <LayoutTemplate size={18} strokeWidth={1.8} /> Templates
          </button>
          {user?.is_admin && (
            <NavLink to="/admin" onClick={() => onOpenChange(false)} data-testid="nav-drawer-admin" className={`${item} text-amber-700 hover:bg-amber-50`}>
              <Shield size={18} strokeWidth={1.8} /> Admin
            </NavLink>
          )}
        </nav>
        {!isStandalone() && (
          <NavLink to="/get-app" onClick={() => { trackAppTap("home_menu"); onOpenChange(false); }} data-testid="nav-drawer-get-app"
            className="mb-3 flex items-center gap-3 p-3 rounded-2xl bg-sky-50 ring-1 ring-sky-200 hover:bg-sky-100 transition-colors">
            <span className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0"><Smartphone size={17} /></span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">Get the app</span>
              <span className="block text-xs text-slate-500">Ishukart on Android &amp; iPhone</span>
            </span>
          </NavLink>
        )}
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className={`${item} text-rose-600 hover:bg-rose-50`}>
          <LogOut size={18} strokeWidth={1.8} /> Log out
        </button>
      </SheetContent>
    </Sheet>
  );
};

export default NavDrawer;
